// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  getBeach, updateBeach,
  loadSpiaggeItExistingIds, importSpiaggeItPlan,
} from '../services/firebase';
import { parseCsv, planRow } from '../utils/spiaggeItMapping';
import Topbar from '../components/layout/Topbar';
import { Panel, PanelBody, PanelHead, Button, Spinner } from '../components/ui';

// ─── Helpers ─────────────────────────────────────────────────────────────────
function StatusBadge({ attiva }) {
    return (
        <span style={{
            display: 'inline-block', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600,
            background: attiva ? 'rgba(22,163,74,0.15)' : 'rgba(107,114,128,0.15)',
            color: attiva ? '#16a34a' : '#6b7280',
            border: `1px solid ${attiva ? '#16a34a40' : '#6b728040'}`,
        }}>
            {attiva ? '✓ Attiva' : '○ Non configurata'}
        </span>
    );
}

const inputStyle = {
    background: 'var(--surface)', border: '1px solid var(--border-mid)',
    borderRadius: 'var(--radius-md)', color: '#fff',
    padding: '8px 12px', fontSize: '13px', fontFamily: 'var(--font-sans)',
    width: '100%', boxSizing: 'border-box',
};

// ─── Pagina principale ────────────────────────────────────────────────────────
export default function SpiaggeIt() {
    const { admin, beach, refreshBeach } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testando, setTestando] = useState(false);
    const [testResult, setTestResult] = useState(null);

    const cfg = beach?.spiaggeIt || {};

    const [form, setForm] = useState({
        attiva: false,
        licenseId: '',
        spotMapping: {},
    });

    // Nuovo mapping in lavorazione
    const [newMapLocal, setNewMapLocal] = useState('');
    const [newMapRemote, setNewMapRemote] = useState('');

    useEffect(() => {
        if (beach) {
            setForm({
                attiva: beach.spiaggeIt?.attiva || false,
                licenseId: beach.spiaggeIt?.licenseId || '',
                spotMapping: beach.spiaggeIt?.spotMapping || {},
            });
            setLoading(false);
        }
    }, [beach]);

    const handleSave = async () => {
        setSaving(true);
        try {
            await updateBeach(admin.beachCode, {
                spiaggeIt: {
                    ...beach.spiaggeIt,
                    attiva: form.attiva,
                    licenseId: form.licenseId,
                    spotMapping: form.spotMapping,
                }
            });
            await refreshBeach();
        } finally {
            setSaving(false);
        }
    };

    const addMapping = () => {
        if (!newMapLocal.trim() || !newMapRemote.trim()) return;
        setForm(p => ({
            ...p,
            spotMapping: { ...p.spotMapping, [newMapLocal.trim()]: newMapRemote.trim() }
        }));
        setNewMapLocal('');
        setNewMapRemote('');
    };

    const removeMapping = (key) => {
        setForm(p => {
            const m = { ...p.spotMapping };
            delete m[key];
            return { ...p, spotMapping: m };
        });
    };

    // ── Import CSV ─────────────────────────────────────────────────────────
    const fileInputRef = useRef(null);
    const [csvFileName, setCsvFileName] = useState('');
    const [preview, setPreview] = useState(null); // { toWrite, skipped, existingCount, total }
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState('');
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState(null); // { ok, failed, errors: [] }

    const handleCsvChosen = async (file) => {
        if (!file) return;
        setPreview(null);
        setImportResult(null);
        setPreviewError('');
        setPreviewLoading(true);
        setCsvFileName(file.name);
        try {
            const text = await file.text();
            const rows = parseCsv(text);
            if (!rows.length) throw new Error('CSV vuoto o non valido');

            const existingIds = await loadSpiaggeItExistingIds(admin.beachCode);
            const plans = rows.map(planRow).map(p => {
                if (p.skip) return p;
                if (existingIds.has(String(p.identificativo))) {
                    return { ...p, skip: 'già importato', skipCategory: 'duplicate' };
                }
                return p;
            });

            const toWrite = plans.filter(p => !p.skip);
            const alreadyImported = plans.filter(p => p.skipCategory === 'duplicate');
            const missingInLayout = plans.filter(p => p.skipCategory === 'missing-layout');
            const cancelledOnSpiagge = plans.filter(p => p.skipCategory === 'cancelled-on-spiagge');
            const errors = plans.filter(p => p.skipCategory === 'error');

            setPreview({
                total: rows.length,
                toWrite,
                alreadyImported,
                missingInLayout,
                cancelledOnSpiagge,
                errors,
            });
        } catch (e) {
            setPreviewError(e.message || 'Errore parsing CSV');
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleImportConfirm = async () => {
        if (!preview?.toWrite?.length) return;
        setImporting(true);
        setImportResult(null);
        const errors = [];
        const unverified = []; // ordini scritti ma layout non aggiornato
        let ok = 0;
        let layoutUpdates = 0;
        const layoutTemplate = beach?.layout || [];
        for (const plan of preview.toWrite) {
            try {
                const res = await importSpiaggeItPlan(admin.beachCode, plan, layoutTemplate);
                ok++;
                if (res?.verified) {
                    layoutUpdates += res.verified.updated;
                    if (res.verified.missing > 0) {
                        unverified.push({
                            id: plan.identificativo,
                            expectedCell: plan.order.celle[0]?.id,
                            missingDates: res.verified.missing,
                            totalDates: res.verified.total,
                        });
                    }
                }
            } catch (e) {
                errors.push({ id: plan.identificativo, msg: e.message });
            }
        }
        setImportResult({ ok, failed: errors.length, errors, layoutUpdates, unverified });
        setImporting(false);
        // NB: prima qui si ricaricava la preview per aggiornare il dedup,
        // ma handleCsvChosen resettava importResult → il banner spariva subito.
        // Ora la preview resta stale finché l'utente non ricarica il file
        // manualmente — trade-off accettabile per preservare il feedback.
    };

    const testWebhook = async () => {
        setTestando(true);
        setTestResult(null);
        try {
            const res = await fetch(
                `https://us-central1-just-salento.cloudfunctions.net/spiaggeItWebhook`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        license: form.licenseId || 'demo2',
                        resource: 'reservation',
                        action: 'create',
                        resourceId: 9999999,
                        _test: true,
                    }),
                }
            );
            setTestResult(res.ok ? 'success' : 'error');
        } catch {
            setTestResult('error');
        } finally {
            setTestando(false);
        }
    };

    if (loading) return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Spinner size={28} />
        </div>
    );

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Topbar
                title="Integrazione Spiagge.it"
                subtitle="Sincronizzazione bidirezionale delle disponibilità"
                actions={
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <StatusBadge attiva={form.attiva && form.licenseId} />
                        <Button variant="primary" onClick={handleSave} disabled={saving}>
                            {saving ? <Spinner size={14} /> : 'Salva'}
                        </Button>
                    </div>
                }
            />

            <div style={{ padding: '24px 28px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' }}>

                {/* ── Stato integrazione ── */}
                <Panel>
                    <PanelHead title="Configurazione" subtitle="Credenziali e attivazione integrazione" />
                    <PanelBody>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                            {/* Toggle attiva */}
                            <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                                <div
                                    onClick={() => setForm(p => ({ ...p, attiva: !p.attiva }))}
                                    style={{
                                        width: '44px', height: '24px', borderRadius: '12px', position: 'relative', cursor: 'pointer',
                                        background: form.attiva ? '#16a34a' : 'var(--border-mid)', transition: 'background 0.2s',
                                    }}
                                >
                                    <div style={{
                                        position: 'absolute', top: '3px', left: form.attiva ? '23px' : '3px',
                                        width: '18px', height: '18px', borderRadius: '50%', background: '#fff',
                                        transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                                    }} />
                                </div>
                                <div>
                                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>
                                        Integrazione {form.attiva ? 'attiva' : 'disattivata'}
                                    </div>
                                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                                        Se attiva, le prenotazioni vengono sincronizzate automaticamente
                                    </div>
                                </div>
                            </label>

                            {/* License ID */}
                            <div>
                                <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                    License ID (fornito da Spiagge.it)
                                </label>
                                <input
                                    placeholder="es. demo2"
                                    value={form.licenseId}
                                    onChange={e => setForm(p => ({ ...p, licenseId: e.target.value }))}
                                    style={{ ...inputStyle, width: '300px' }}
                                />
                                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '5px' }}>
                                    Trovato nel pannello Spiagge.it → Impostazioni → API
                                </div>
                            </div>

                            {/* Credenziali */}
                            <div style={{ background: 'rgba(31,147,224,0.06)', border: '1px solid rgba(31,147,224,0.2)', borderRadius: 'var(--radius-md)', padding: '14px' }}>
                                <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--ocean)', marginBottom: '8px' }}>
                                    🔐 Credenziali API (Firebase Secrets)
                                </div>
                                <div style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.7 }}>
                                    Le chiavi <code>SPIAGGE_CLIENT_ID</code> e <code>SPIAGGE_CLIENT_SECRET</code> sono
                                    salvate come Firebase Secrets e non visibili qui per sicurezza.<br />
                                    Per aggiornarle: <code>firebase functions:secrets:set SPIAGGE_CLIENT_ID</code>
                                </div>
                            </div>

                            {/* Webhook URL */}
                            <div>
                                <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                    URL Webhook da comunicare a Spiagge.it
                                </label>
                                <div style={{
                                    background: 'var(--surface)', border: '1px solid var(--border-mid)',
                                    borderRadius: 'var(--radius-md)', padding: '10px 14px',
                                    fontFamily: 'monospace', fontSize: '12px', color: 'var(--ocean)',
                                    wordBreak: 'break-all',
                                }}>
                                    https://us-central1-just-salento.cloudfunctions.net/spiaggeItWebhook
                                </div>
                                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '5px' }}>
                                    Comunica questo URL al supporto Spiagge.it per attivare i webhook
                                </div>
                            </div>

                        </div>
                    </PanelBody>
                </Panel>

                {/* ── Mappatura celle ── */}
                <Panel>
                    <PanelHead
                        title="Mappatura posti"
                        subtitle="Associa ogni cella della tua mappa al nome del posto su Spiagge.it"
                    />
                    <PanelBody>
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '16px', lineHeight: 1.6 }}>
                            I posti sulla tua mappa (es. <strong>A1</strong>, <strong>B3</strong>) corrispondono
                            a nomi diversi su Spiagge.it (es. <strong>10</strong>, <strong>2B</strong>).
                            Aggiungi qui la corrispondenza per ogni posto che vuoi sincronizzare.
                        </div>

                        {/* Form aggiunta */}
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', marginBottom: '16px' }}>
                            <div style={{ flex: 1 }}>
                                <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>
                                    ID cella (tua mappa)
                                </label>
                                <input
                                    placeholder="es. A1"
                                    value={newMapLocal}
                                    onChange={e => setNewMapLocal(e.target.value.toUpperCase())}
                                    style={inputStyle}
                                />
                            </div>
                            <div style={{ color: 'var(--text-muted)', paddingBottom: '10px', fontSize: '16px' }}>→</div>
                            <div style={{ flex: 1 }}>
                                <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>
                                    spotName (Spiagge.it)
                                </label>
                                <input
                                    placeholder="es. 10"
                                    value={newMapRemote}
                                    onChange={e => setNewMapRemote(e.target.value)}
                                    style={inputStyle}
                                />
                            </div>
                            <Button variant="primary" onClick={addMapping} disabled={!newMapLocal || !newMapRemote}
                                style={{ paddingBottom: '10px' }}>
                                + Aggiungi
                            </Button>
                        </div>

                        {/* Lista mappature */}
                        {Object.keys(form.spotMapping).length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted)', fontSize: '13px' }}>
                                Nessuna mappatura configurata
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                {Object.entries(form.spotMapping).map(([local, remote]) => (
                                    <div key={local} style={{
                                        display: 'flex', alignItems: 'center', gap: '12px',
                                        background: 'var(--surface)', border: '1px solid var(--border)',
                                        borderRadius: 'var(--radius-md)', padding: '10px 14px',
                                    }}>
                                        <span style={{ fontFamily: 'monospace', fontSize: '13px', fontWeight: 600, color: 'var(--ocean)', minWidth: '60px' }}>{local}</span>
                                        <span style={{ color: 'var(--text-muted)' }}>→</span>
                                        <span style={{ fontFamily: 'monospace', fontSize: '13px', color: 'var(--text-soft)', flex: 1 }}>{remote}</span>
                                        <button onClick={() => removeMapping(local)} style={{
                                            width: '24px', height: '24px', borderRadius: '50%', border: '1px solid var(--border)',
                                            background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '13px',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}>×</button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </PanelBody>
                </Panel>

                {/* ── Import CSV manuale ── */}
                <Panel>
                    <PanelHead
                        title="Import CSV manuale"
                        subtitle="Carica l'export prenotazioni da spiagge.it → prenotazioni bloccate su Kalé Cora"
                    />
                    <PanelBody>
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '16px', lineHeight: 1.7 }}>
                            Scarica il CSV da <strong>spiagge.it → Prenotazioni Online → Esporta</strong>,
                            poi caricalo qui. Ogni riga viene mappata a una cella Kalé Cora e importata come
                            ordine <em>bloccato</em>, così il canale app non doppia i posti già venduti sul portale.
                            L'import è idempotente: le prenotazioni già importate vengono saltate automaticamente.
                        </div>

                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '16px' }}>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".csv,text/csv"
                                onChange={(e) => handleCsvChosen(e.target.files?.[0])}
                                style={{ display: 'none' }}
                            />
                            <Button variant="ghost" onClick={() => fileInputRef.current?.click()} disabled={previewLoading || importing}>
                                {previewLoading ? <Spinner size={14} /> : '📄 Scegli CSV'}
                            </Button>
                            {csvFileName && (
                                <span style={{ fontSize: '12px', color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                                    {csvFileName}
                                </span>
                            )}
                        </div>

                        {previewError && (
                            <div style={{
                                background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.3)',
                                borderRadius: 'var(--radius-md)', padding: '10px 14px',
                                fontSize: '12px', color: '#dc2626', marginBottom: '12px',
                            }}>
                                ⚠️ {previewError}
                            </div>
                        )}

                        {preview && (
                            <>
                                {/* Riepilogo contatori */}
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '16px' }}>
                                    <CounterCell label="Totale righe" value={preview.total} color="var(--text-soft)" />
                                    <CounterCell label="Da importare" value={preview.toWrite.length} color="#16a34a" />
                                    <CounterCell label="Già importate" value={preview.alreadyImported.length} color="var(--text-muted)" />
                                    <CounterCell label="Errori" value={preview.errors.length} color={preview.errors.length ? '#dc2626' : 'var(--text-muted)'} />
                                </div>

                                {/* Tabella righe da scrivere */}
                                {preview.toWrite.length > 0 && (
                                    <div style={{ marginBottom: '16px' }}>
                                        <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>
                                            Nuove prenotazioni ({preview.toWrite.length})
                                        </div>
                                        <PreviewTable rows={preview.toWrite} kind="new" />
                                    </div>
                                )}

                                {/* Cancellate su spiagge.it */}
                                {preview.cancelledOnSpiagge?.length > 0 && (
                                    <div style={{ marginBottom: '16px' }}>
                                        <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>
                                            Cancellate su spiagge.it ({preview.cancelledOnSpiagge.length}) — non importate
                                        </div>
                                        <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '6px', fontStyle: 'italic' }}>
                                            Il cliente ha annullato la prenotazione su spiagge.it. Non vanno bloccate su KC.
                                        </div>
                                        <PreviewTable rows={preview.cancelledOnSpiagge} kind="error" />
                                    </div>
                                )}

                                {/* Posti non presenti nel layout KC — informativo, non allarmante */}
                                {preview.missingInLayout.length > 0 && (
                                    <div style={{ marginBottom: '16px' }}>
                                        <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>
                                            Posti non nel layout Kalé Cora ({preview.missingInLayout.length}) — ignorabili
                                        </div>
                                        <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '6px', fontStyle: 'italic' }}>
                                            Prenotazioni per postazioni che esistono solo su spiagge.it (es. fila stagionali, ombrelloni convenzionati).
                                            Non sono prenotabili dall'app, quindi non c'è rischio di doppia prenotazione.
                                        </div>
                                        <PreviewTable rows={preview.missingInLayout} kind="error" />
                                    </div>
                                )}

                                {/* Tabella righe con errori veri */}
                                {preview.errors.length > 0 && (
                                    <div style={{ marginBottom: '16px' }}>
                                        <div style={{ fontSize: '11px', color: '#dc2626', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>
                                            Errori mappatura ({preview.errors.length})
                                        </div>
                                        <PreviewTable rows={preview.errors} kind="error" />
                                    </div>
                                )}

                                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', paddingTop: '8px', borderTop: '1px solid var(--border)' }}>
                                    <Button
                                        variant="primary"
                                        onClick={handleImportConfirm}
                                        disabled={importing || preview.toWrite.length === 0}
                                    >
                                        {importing
                                            ? <><Spinner size={14} /> &nbsp;Importo…</>
                                            : preview.toWrite.length
                                                ? `Importa ${preview.toWrite.length} prenotazioni`
                                                : 'Nessuna nuova da importare'}
                                    </Button>
                                </div>

                                {/* Banner successo evidente */}
                                {importResult && (
                                    <div style={{
                                        marginTop: '16px',
                                        background: importResult.failed
                                            ? 'rgba(220,38,38,0.08)'
                                            : 'rgba(22,163,74,0.12)',
                                        border: `1px solid ${importResult.failed ? 'rgba(220,38,38,0.4)' : 'rgba(22,163,74,0.4)'}`,
                                        borderRadius: 'var(--radius-md)',
                                        padding: '16px 20px',
                                        display: 'flex', alignItems: 'flex-start', gap: '14px',
                                    }}>
                                        <div style={{ fontSize: '28px', lineHeight: 1 }}>
                                            {importResult.failed ? '⚠️' : '✅'}
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: '15px', fontWeight: 600, color: importResult.failed ? '#dc2626' : '#16a34a', marginBottom: '4px' }}>
                                                {importResult.failed
                                                    ? `Importazione completata con errori`
                                                    : `Importazione completata`}
                                            </div>
                                            <div style={{ fontSize: '13px', color: 'var(--text-soft)', lineHeight: 1.6 }}>
                                                <strong>{importResult.ok}</strong> prenotazioni scritte in Firestore
                                                {importResult.layoutUpdates > 0 && (
                                                    <> · <strong>{importResult.layoutUpdates}</strong> celle giornaliere aggiornate sulla mappa</>
                                                )}
                                                {importResult.failed > 0 && (
                                                    <> · <span style={{ color: '#dc2626' }}><strong>{importResult.failed}</strong> errori</span></>
                                                )}
                                            </div>
                                            {importResult.unverified?.length > 0 && (
                                                <div style={{ marginTop: '10px', padding: '10px 12px', background: 'rgba(255,165,0,0.1)', border: '1px solid rgba(255,165,0,0.3)', borderRadius: '6px', fontSize: '12px', color: 'var(--text-soft)' }}>
                                                    ⚠️ <strong>{importResult.unverified.length} ordini scritti ma la mappa non li rispecchia.</strong>
                                                    <details style={{ marginTop: '4px' }}>
                                                        <summary style={{ cursor: 'pointer', color: 'var(--text-muted)' }}>Dettagli</summary>
                                                        <div style={{ marginTop: '6px', fontFamily: 'monospace', fontSize: '11px', color: 'var(--text-muted)' }}>
                                                            {importResult.unverified.slice(0, 10).map(u => (
                                                                <div key={u.id}>
                                                                    #{u.id} · atteso {u.expectedCell} · {u.missingDates}/{u.totalDates} date non aggiornate
                                                                </div>
                                                            ))}
                                                            {importResult.unverified.length > 10 && <div>… e altri {importResult.unverified.length - 10}</div>}
                                                        </div>
                                                        <div style={{ marginTop: '8px', color: 'var(--text-muted)', fontSize: '11px' }}>
                                                            Apri la Console del browser (F12) per il log dettagliato.
                                                        </div>
                                                    </details>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {importResult?.errors?.length > 0 && (
                                    <div style={{ marginTop: '12px', fontSize: '11px', color: '#dc2626', fontFamily: 'monospace' }}>
                                        {importResult.errors.slice(0, 5).map(e => (
                                            <div key={e.id}>#{e.id}: {e.msg}</div>
                                        ))}
                                        {importResult.errors.length > 5 && <div>… e altri {importResult.errors.length - 5}</div>}
                                    </div>
                                )}
                            </>
                        )}
                    </PanelBody>
                </Panel>

                {/* ── Test integrazione ── */}
                <Panel>
                    <PanelHead title="Test integrazione" subtitle="Verifica che il webhook sia raggiungibile" />
                    <PanelBody>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <Button variant="ghost" onClick={testWebhook} disabled={testando}>
                                {testando ? <Spinner size={14} /> : '🔌 Testa webhook'}
                            </Button>
                            {testResult === 'success' && (
                                <span style={{ fontSize: '13px', color: '#16a34a' }}>✅ Webhook raggiungibile</span>
                            )}
                            {testResult === 'error' && (
                                <span style={{ fontSize: '13px', color: '#dc2626' }}>❌ Errore — controlla i log Firebase</span>
                            )}
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '10px', lineHeight: 1.6 }}>
                            Puoi anche testare con il webhook emulator di Spiagge.it usando l'URL sopra e il license ID configurato.
                        </div>
                    </PanelBody>
                </Panel>

                {/* ── Come funziona ── */}
                <Panel>
                    <PanelHead title="Come funziona" subtitle="Flusso di sincronizzazione bidirezionale" />
                    <PanelBody>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                            <div>
                                <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--ocean)', marginBottom: '10px' }}>
                                    📱 App → Spiagge.it
                                </div>
                                <div style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.8 }}>
                                    1. Utente prenota sull'app<br />
                                    2. Stripe conferma il pagamento<br />
                                    3. Cloud Function crea la prenotazione su Spiagge.it<br />
                                    4. Il posto appare occupato anche sul loro gestionale
                                </div>
                            </div>
                            <div>
                                <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--ocean)', marginBottom: '10px' }}>
                                    🌐 Spiagge.it → App
                                </div>
                                <div style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.8 }}>
                                    1. Cliente prenota su Spiagge.it<br />
                                    2. Webhook notifica la Cloud Function<br />
                                    3. Cella marcata come occupata su Firestore<br />
                                    4. Il posto non è più prenotabile sull'app
                                </div>
                            </div>
                        </div>
                    </PanelBody>
                </Panel>

            </div>
        </div>
    );
}

// ─── Sub-componenti Import ────────────────────────────────────────────────────

function CounterCell({ label, value, color }) {
    return (
        <div style={{
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)', padding: '10px 12px',
        }}>
            <div style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                {label}
            </div>
            <div style={{ fontSize: '20px', fontWeight: 600, color, marginTop: '4px', fontFamily: 'var(--font-serif)' }}>
                {value}
            </div>
        </div>
    );
}

function PreviewTable({ rows, kind }) {
    const isError = kind === 'error';
    return (
        <div style={{
            border: '1px solid var(--border)', borderRadius: 'var(--radius-md)',
            maxHeight: '260px', overflowY: 'auto',
            fontSize: '12px',
        }}>
            {rows.map((p, i) => (
                <div key={p.identificativo}
                    style={{
                        display: 'grid',
                        gridTemplateColumns: isError ? '110px 1fr 1fr' : '110px 1fr 60px 1fr 80px',
                        gap: '10px', padding: '8px 12px',
                        borderBottom: i < rows.length - 1 ? '1px solid var(--border)' : 'none',
                        alignItems: 'center',
                        background: i % 2 ? 'transparent' : 'rgba(255,255,255,0.02)',
                    }}
                >
                    <span style={{ fontFamily: 'monospace', color: 'var(--text-muted)' }}>
                        #{p.identificativo}
                    </span>
                    {isError ? (
                        <>
                            <span style={{ color: 'var(--text-soft)' }}>—</span>
                            <span style={{ color: '#dc2626' }}>{p.skip}</span>
                        </>
                    ) : (
                        <>
                            <span style={{ color: 'var(--text-soft)' }}>{p.order.guestName}</span>
                            <span style={{ fontFamily: 'monospace', fontWeight: 600, color: 'var(--ocean)' }}>
                                {p.order.celle[0].id}
                                {p.mapped.isArea && <span style={{ opacity: 0.6, marginLeft: '4px' }}>·area</span>}
                            </span>
                            <span style={{ color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: '11px' }}>
                                {p.dates.length > 1
                                    ? `${p.dates[0]} → ${p.dates[p.dates.length - 1]} (${p.dates.length}gg)`
                                    : p.dates[0]}
                            </span>
                            <span style={{ color: 'var(--text-soft)', textAlign: 'right' }}>
                                €{p.order.totalPrice}
                            </span>
                        </>
                    )}
                </div>
            ))}
        </div>
    );
}